<template>
  <transition name="a-alert-fade">
    <div v-if="visible" class="a-alert" :class="rootClass" role="alert">
      <div v-if="showIcon" class="a-alert__icon">
        <v-icon :size="iconSize" class="a-alert__glyph">{{ resolvedIcon }}</v-icon>
      </div>

      <div class="a-alert__body">
        <span v-if="resolvedTitle" class="a-alert__title">{{ resolvedTitle }}</span>
        <!-- Default slot wins over `messages` when the caller needs markup. -->
        <slot>
          <span
            v-for="(line, index) in lines"
            :key="index"
            class="a-alert__text"
          >{{ line }}</span>
        </slot>
      </div>

      <button
        v-if="dismissible"
        type="button"
        class="a-alert__close"
        aria-label="Dismiss"
        @click="$emit('dismiss')"
      >
        <v-icon size="16" class="a-alert__glyph">mdi-close</v-icon>
      </button>
    </div>
  </transition>
</template>

<script>
/**
 * AAlertBanner — the single warning/danger/success/info banner used by every
 * page that surfaces `alertError` / `alertDanger` style messages.
 *
 * Every page previously carried its own copy of this markup plus ~60 lines of
 * CSS under a page-local prefix (`rv-alert`, `gi-alert`, `claim-alert`, …),
 * which is why the banners drifted apart and several of them rendered
 * unreadable in dark mode. This component is the one place to change them now.
 *
 * Colours come entirely from the theme tokens in plugins/theme-dark.css
 * (--app-warning-*, --app-danger-*, …). Those tokens are redefined under
 * `.dark-mode`, so the banner follows the theme with NO dark-mode rules of its
 * own — retheming every banner in the app is a token edit, not a CSS sweep.
 *
 * Usage:
 *   <AAlertBanner :messages="alertError" />                        warning
 *   <AAlertBanner variant="danger" :messages="alertDanger" />      danger
 *   <AAlertBanner variant="danger" title="Alert" solid />          saturated fill
 *   <AAlertBanner :messages="errs" title="Heads up" icon="mdi-flag" dense />
 *
 * The banner renders nothing while `messages` is empty (and no slot content is
 * supplied), so callers do not need their own `v-if`.
 */

// Per-variant defaults. Add a variant here and it is available everywhere.
const VARIANTS = {
  warning: { title: "Warning", icon: "mdi-alert-outline" },
  danger: { title: "Alert", icon: "mdi-alert-circle-outline" },
  success: { title: "Success", icon: "mdi-check-circle-outline" },
  info: { title: "Note", icon: "mdi-information-outline" },
  neutral: { title: "", icon: "mdi-information-outline" },
};

export default {
  name: "AAlertBanner",

  props: {
    /** warning | danger | success | info | neutral */
    variant: {
      type: String,
      default: "warning",
      validator: (v) => Object.keys(VARIANTS).includes(v),
    },
    /** Message line(s). A plain string renders as a single line. */
    messages: {
      type: [Array, String],
      default: () => [],
    },
    /** Overrides the variant's default label. */
    title: {
      type: String,
      default: null,
    },
    /** Drops the uppercase label row entirely. */
    hideTitle: {
      type: Boolean,
      default: false,
    },
    /** Overrides the variant's default mdi icon. */
    icon: {
      type: String,
      default: null,
    },
    showIcon: {
      type: Boolean,
      default: true,
    },
    iconSize: {
      type: [Number, String],
      default: 20,
    },
    /** Tighter padding, for banners inside dialogs and side panels. */
    dense: {
      type: Boolean,
      default: false,
    },
    /** Saturated fill instead of the soft wash. Reads louder; use sparingly. */
    solid: {
      type: Boolean,
      default: false,
    },
    dismissible: {
      type: Boolean,
      default: false,
    },
    /** Force the banner open even with no messages (e.g. slot-only usage). */
    forceShow: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    lines() {
      if (!this.messages) return [];
      const list = Array.isArray(this.messages) ? this.messages : [this.messages];
      return list.filter((line) => line !== null && line !== undefined && line !== "");
    },

    visible() {
      return this.forceShow || this.lines.length > 0 || !!this.$slots.default;
    },

    rootClass() {
      return [
        `a-alert--${this.variant}`,
        { "a-alert--solid": this.solid, "a-alert--dense": this.dense },
      ];
    },

    resolvedTitle() {
      if (this.hideTitle) return "";
      const fallback = (VARIANTS[this.variant] || VARIANTS.warning).title;
      return this.title === null ? fallback : this.title;
    },

    resolvedIcon() {
      return this.icon || (VARIANTS[this.variant] || VARIANTS.warning).icon;
    },
  },
};
</script>

<style scoped>
/* ============================================================
   Every colour below resolves from a --a-alert-* custom property
   set by the variant class, which in turn points at a theme token.
   Light/dark is handled by the tokens, not by rules in here.
   ============================================================ */
.a-alert {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 14px 18px;
  margin-bottom: 16px;
  border-radius: 12px;
  font-family: "Lato", "Segoe UI", -apple-system, BlinkMacSystemFont, sans-serif;
  background: var(--a-alert-bg);
  border: 1px solid var(--a-alert-border);
}

.a-alert--dense {
  gap: 10px;
  padding: 10px 14px;
  margin-bottom: 12px;
  border-radius: 10px;
}

.a-alert__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: var(--a-alert-icon-bg);
  border: 1px solid var(--a-alert-border);
}

.a-alert--dense .a-alert__icon {
  width: 28px;
  height: 28px;
  border-radius: 8px;
}

.a-alert__glyph {
  color: var(--a-alert-accent) !important;
}

/* Typography lives on the body so default-slot content inherits it without
   the caller needing any of the component's (scoped) inner classes. */
.a-alert__body {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding-top: 2px;
  min-width: 0;
  color: var(--a-alert-fg);
  font-size: 0.88rem;
  font-weight: 600;
  line-height: 1.45;
}

.a-alert__title {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 1.8px;
  text-transform: uppercase;
  color: var(--a-alert-fg);
  opacity: 0.75;
}

.a-alert__text {
  display: block;
  word-break: break-word;
}

.a-alert--dense .a-alert__body {
  font-size: 0.82rem;
}

.a-alert__close {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  margin-top: 4px;
  border-radius: 8px;
  background: transparent;
  transition: background 0.15s ease;
}

.a-alert__close:hover {
  background: var(--a-alert-icon-bg);
}

/* ---------- Variants ---------- */
.a-alert--warning {
  --a-alert-bg: var(--app-warning-bg, #fef3c7);
  --a-alert-border: var(--app-warning-border, #fde68a);
  --a-alert-icon-bg: var(--app-warning-bg-strong, #fde68a);
  --a-alert-accent: var(--app-warning, #f59e0b);
  --a-alert-fg: var(--app-warning-fg, #b45309);
}

.a-alert--danger {
  --a-alert-bg: var(--app-danger-bg, #fef2f2);
  --a-alert-border: var(--app-danger-border, #fecaca);
  --a-alert-icon-bg: var(--app-danger-bg-strong, #fee2e2);
  --a-alert-accent: var(--app-danger, #ef4444);
  --a-alert-fg: var(--app-danger-fg, #991b1b);
}

.a-alert--success {
  --a-alert-bg: var(--app-success-bg, #ecfdf5);
  --a-alert-border: var(--app-success-border, #bbf7d0);
  --a-alert-icon-bg: var(--app-success-border, #bbf7d0);
  --a-alert-accent: var(--app-success, #10b981);
  --a-alert-fg: var(--app-success-fg, #065f46);
}

.a-alert--info {
  --a-alert-bg: var(--app-info-bg, #e0f2fe);
  --a-alert-border: var(--app-accent-border, #bae6fd);
  --a-alert-icon-bg: var(--app-accent-soft, #bae6fd);
  --a-alert-accent: var(--app-accent, #0ea5e9);
  --a-alert-fg: var(--app-info-fg, #0369a1);
}

.a-alert--neutral {
  --a-alert-bg: var(--app-neutral-bg, #ececec);
  --a-alert-border: var(--app-border, #e2e8f0);
  --a-alert-icon-bg: var(--app-hover, #e2e8f0);
  --a-alert-accent: var(--app-text-muted, #64748b);
  --a-alert-fg: var(--app-neutral-fg, #555555);
}

/* ---------- Solid fill ---------- */
.a-alert--solid {
  background: var(--a-alert-accent);
  border-color: var(--a-alert-accent);
}

.a-alert--solid .a-alert__icon {
  background: var(--app-veil, rgba(255, 255, 255, 0.18));
  border-color: var(--app-veil-border, rgba(255, 255, 255, 0.28));
}

.a-alert--solid .a-alert__glyph,
.a-alert--solid .a-alert__title,
.a-alert--solid .a-alert__text,
.a-alert--solid .a-alert__body {
  color: var(--app-on-solid, #ffffff) !important;
}

/* ---------- Transition ---------- */
.a-alert-fade-enter-active,
.a-alert-fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.a-alert-fade-enter,
.a-alert-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

@media (max-width: 600px) {
  .a-alert {
    gap: 10px;
    padding: 12px 14px;
  }

  .a-alert__icon {
    width: 30px;
    height: 30px;
  }
}
</style>
